import { authSelectors } from 'ducks/auth'
import { CommonStipulationType, MarketplaceProductDetailsType } from 'ducks/myEnergy'
import PricingOptionDetailsRow from 'myenergy/selectionComponent/checkoutFinancing/financeMarketplace/PricingOptionDetailsRow'
import {
  Alert,
  BaseThemeType,
  Button,
  CheckFilledIcon,
  Chip,
  MenuItem,
  Select,
  styled,
  Typography,
  useMediaQuery,
} from 'opensolar-ui'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { formatCurrencyWithSymbol } from 'util/misc'

type PropTypes = {
  decision: 'Pending' | 'Conditionally approved' | 'Approved' | 'Declined'
  onClose: () => void
  stips?: CommonStipulationType[]
  submittedDate?: string
  marketplaceProductDetails: MarketplaceProductDetailsType
}

const Wrapper = styled('div')(() => ({
  margin: '10px 0px 20px 0px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  gap: '32px',
}))

const LogoWrapper = styled('div')(() => ({
  marginBottom: '20px',
}))

const DecisionRow = styled('div')(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  justifyContent: isMobile ? 'flex-start' : 'center',
  alignItems: 'center',
  textAlign: 'center',
  gap: isMobile ? '8px' : '16px',
}))

const DetailsSection = styled('div')(({ theme }: { theme: BaseThemeType }) => ({
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: '20px',
  padding: '20px 60px',
  border: `1px solid ${theme.palette.grey[400]}`,
  borderRadius: '12px',
  margin: '20px',
}))

const NextStepsRow = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  gap: '16px',
  width: '100%',
  borderBottom: `1px solid #DCDCDE`,
  paddingBottom: '16px',
}))

const NextSteps = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  fontSize: '16px',
  color: '#757575',
  textAlign: 'center',
  fontWeight: 600,
  letterSpacing: '-0.16px',
}))

const LoanAmountRow = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '6px',
  width: '100%',
})

const LoanAmountLabel = styled(Typography)({
  fontWeight: 600,
})

const LoanAmountText = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  color: theme.palette.blue[500],
  fontWeight: 600,
  textAlign: 'center',
  letterSpacing: '-0.16px',
}))

const StyledCheckFilledIcon = styled(CheckFilledIcon)(({ theme }: { theme: BaseThemeType }) => ({
  color: theme.palette.green[800],
  fontSize: '86px',
  height: '86px',
  width: '86px',
  marginBottom: '16px',
}))

const DeclineExplanation = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  margin: '0px 45px',
  textAlign: 'left',
}))

const ButtonRow = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  textAlign: 'center',
  gap: '16px',
  width: '100%',
  marginRight: '45px',
}))

const MarketplaceDecisionExplanation: React.FunctionComponent<PropTypes> = (props) => {
  const [decisionOverride, setDecisionOverride] = useState<
    'Pending' | 'Conditionally approved' | 'Approved' | 'Declined' | undefined
  >(undefined)
  const [hideOverride, setHideOverride] = useState(false)
  const isStaff = useSelector(authSelectors.getIsStaff)
  const isTestEnv = !['production', 'staging4'].includes(window.ENV)
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))

  const decision = useMemo(() => {
    if (decisionOverride) return decisionOverride
    return props.decision
  }, [props.decision, decisionOverride])

  const chipSeverity = useMemo(() => {
    if (decision === 'Approved' || decision === 'Conditionally approved') {
      return 'success'
    } else if (decision === 'Declined') {
      return 'error'
    } else {
      return 'warning'
    }
  }, [decision])

  // currently this component is only used for Propensio. When propensio customers are approved
  // we move them onto lender-based milestones. TO ensure their proposal reflects that update we need to
  // reload the entire page when they close this dialog
  useEffect(() => {
    return () => {
      if (decision === 'Approved' || decision === 'Conditionally approved') {
        window.location.reload()
      }
    }
  }, [decision])

  return (
    <Wrapper>
      {isStaff && isTestEnv && !hideOverride && (
        <Alert severity="info">
          <>
            <Typography textVariant="body1">
              Decision Override - only shown in test envs for staff users. This lets you test the different decision
              displays
            </Typography>
            <Select
              onChange={(e) => {
                setDecisionOverride(e?.target?.value as 'Pending' | 'Conditionally approved' | 'Approved' | 'Declined')
              }}
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Conditionally approved">Conditionally approved</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Declined">Declined</MenuItem>
            </Select>
            <Button onClick={() => setHideOverride(true)}>Hide Override</Button>
          </>
        </Alert>
      )}
      <LogoWrapper>
        <img src={props.marketplaceProductDetails?.logo_url} height="67px" width="auto" alt="logo" />
      </LogoWrapper>
      <DecisionRow isMobile={isMobile}>
        <Typography textVariant="h5">Loan Application Status:</Typography>
        <Chip color={chipSeverity} label={decision} />
      </DecisionRow>
      {['Approved', 'Conditionally approved']?.includes(decision) && (
        <>
          <StyledCheckFilledIcon />
          <LoanAmountRow>
            <LoanAmountLabel textVariant="h5">Loan Amount</LoanAmountLabel>
            <LoanAmountText textVariant="h5">
              {formatCurrencyWithSymbol(props.marketplaceProductDetails?.financed_amount || 0, '£', 2)}
            </LoanAmountText>
          </LoanAmountRow>
          <PricingOptionDetailsRow
            pricingOption={{
              term: props.marketplaceProductDetails?.loan_term_months || 0,
              currency_symbol: '£',
              apr: props.marketplaceProductDetails?.interest_rate || 0,
              monthly_payment: props.marketplaceProductDetails?.monthly_payment || 0,
              total_repayable: props.marketplaceProductDetails?.total_repayable || 0,
            }}
          />
        </>
      )}
      {decision === 'Declined' && (
        <DeclineExplanation textVariant="body1">
          Propensio was unable to approve your application for a loan at this time. This decision was based on a
          combination of information provided by you in the application and information received from credit reference
          agencies.
        </DeclineExplanation>
      )}
      <DetailsSection>
        {!['Declined', 'Approved', 'Conditionally approved'].includes(decision) && (
          <>
            <NextStepsRow>
              <NextSteps textVariant="h6">Next Steps</NextSteps>
            </NextStepsRow>
            <Typography textVariant="body1">
              Thank you for your application. We need to take a closer look and may need some additional information
              from you to reach a decision.
            </Typography>
            <Typography textVariant="body1">Here's what you can expect next:</Typography>
            <ul>
              <li>
                <Typography textVariant="body1">
                  You will receive an email from Propensio within the next 15 minutes outlining the next steps. If you
                  haven’t received anything, please check your junk and spam folders.
                </Typography>
              </li>
              <li>
                <Typography textVariant="body1">
                  If we need to speak with you, an Underwriter will contact via phone or email to confirm any
                  outstanding details.
                </Typography>
              </li>
              <li>
                <Typography textVariant="body1">
                  You will receive an email notification once a decision has been made.
                </Typography>
              </li>
              <li>
                <Typography textVariant="body1">
                  You can return to this page at any time to check for updates.
                </Typography>
              </li>
            </ul>
          </>
        )}
        {decision === 'Declined' && (
          <>
            <NextStepsRow>
              <NextSteps textVariant="h6">"What Happens Now?"</NextSteps>
            </NextStepsRow>
            <ul>
              <li>
                <Typography textVariant="body1">
                  Propensio has sent you an email with additional information and resources that you may find helpful.
                </Typography>
              </li>
              <li>
                <Typography textVariant="body1">
                  If you have any further questions after reviewing the email, you may contact Propensio's team at 01709
                  808990.
                </Typography>
              </li>
            </ul>
          </>
        )}
        {(decision === 'Approved' || decision === 'Conditionally approved') && (
          <>
            <NextStepsRow>
              <NextSteps textVariant="h6">Next Steps</NextSteps>
            </NextStepsRow>
            <ul>
              {decision === 'Conditionally approved' && (
                <li>
                  <Typography textVariant="body1">
                    Check your inbox! An email has been sent to you with more information and a request for further
                    documentation.
                  </Typography>
                </li>
              )}
              {decision === 'Approved' && (
                <li>
                  <Typography textVariant="body1">
                    Check your inbox! An email has been sent to you with more information and your credit agreement to
                    sign.
                  </Typography>
                </li>
              )}
              <li>
                <Typography textVariant="body1">
                  You can return to this Finance Explorer at any time to check for updates on your application status
                </Typography>
              </li>
              <li>
                <Typography textVariant="body1">
                  Closing this dialog will cause your proposal to automatically refresh. You can then accept the
                  proposal if you haven't already.
                </Typography>
              </li>
            </ul>
          </>
        )}
      </DetailsSection>
      <ButtonRow>
        <Button onClick={props.onClose} variant="contained">
          Close
        </Button>
      </ButtonRow>
    </Wrapper>
  )
}
export default MarketplaceDecisionExplanation
